import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from './ui/password-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Envelope, CheckCircle, PaperPlaneTilt, CircleNotch, Warning, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailSettingsCard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    provider: '',
    api_key: '',
    sender_email: '',
    sender_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    is_configured: false,
    is_verified: false
  });
  const [maskedKey, setMaskedKey] = useState('');
  const [maskedSmtp, setMaskedSmtp] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/email/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({
          ...prev,
          provider: data.provider || '',
          sender_email: data.sender_email || '',
          sender_name: data.sender_name || '',
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || '',
          is_configured: data.is_configured || false,
          is_verified: data.is_verified || false,
          api_key: '',
          smtp_password: ''
        }));
        setMaskedKey(data.api_key_masked || '');
        setMaskedSmtp(data.smtp_password_masked || '');
      }
    } catch {
      // Settings not configured yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.provider) {
      toast.error('Please select an email provider');
      return;
    }
    if (!settings.sender_email) {
      toast.error('Sender email is required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/email/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      toast.success('Email settings saved');
      fetchSettings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/email/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Test failed');
      toast.success(data.message);
      fetchSettings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid="email-settings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-medium">Email Configuration</h3>
          {settings.is_configured && (
            <Badge className={settings.is_verified ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}>
              {settings.is_verified ? <><CheckCircle size={12} className="mr-1" /> Verified</> : <><Warning size={12} className="mr-1" /> Not Verified</>}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Provider</Label>
          <Select value={settings.provider} onValueChange={(v) => setSettings(s => ({ ...s, provider: v }))}>
            <SelectTrigger data-testid="email-provider-select">
              <SelectValue placeholder="Select email provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sendgrid">SendGrid</SelectItem>
              <SelectItem value="resend">Resend</SelectItem>
              <SelectItem value="smtp">SMTP (Gmail, Outlook, etc.)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {settings.provider && settings.provider !== 'smtp' && (
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              API Key {maskedKey && <span className="text-muted-foreground font-normal normal-case">(Current: {maskedKey})</span>}
            </Label>
            <div className="relative">
              <Input
                type={showApiKey ? 'text' : 'password'}
                placeholder={maskedKey ? 'Leave blank to keep current key' : 'Enter API key'}
                value={settings.api_key}
                onChange={(e) => setSettings(s => ({ ...s, api_key: e.target.value }))}
                data-testid="email-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showApiKey ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        )}

        {settings.provider === 'smtp' && (
          <>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">SMTP Host</Label>
              <Input
                placeholder="smtp.gmail.com"
                value={settings.smtp_host}
                onChange={(e) => setSettings(s => ({ ...s, smtp_host: e.target.value }))}
                data-testid="smtp-host-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">SMTP Port</Label>
              <Input
                type="number"
                placeholder="587"
                value={settings.smtp_port}
                onChange={(e) => setSettings(s => ({ ...s, smtp_port: parseInt(e.target.value) || 587 }))}
                data-testid="smtp-port-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">SMTP Username</Label>
              <Input
                placeholder="your-email@gmail.com"
                value={settings.smtp_username}
                onChange={(e) => setSettings(s => ({ ...s, smtp_username: e.target.value }))}
                data-testid="smtp-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                SMTP Password {maskedSmtp && <span className="text-muted-foreground font-normal normal-case">(Set)</span>}
              </Label>
              <PasswordInput
                placeholder={maskedSmtp ? 'Leave blank to keep current' : 'App password'}
                value={settings.smtp_password}
                onChange={(e) => setSettings(s => ({ ...s, smtp_password: e.target.value }))}
                data-testid="smtp-password-input"
              />
            </div>
          </>
        )}

        <div className="space-y-2">
          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Sender Email</Label>
          <Input
            type="email"
            placeholder="orders@yourcompany.com"
            value={settings.sender_email}
            onChange={(e) => setSettings(s => ({ ...s, sender_email: e.target.value }))}
            data-testid="sender-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Sender Name</Label>
          <Input
            placeholder="Your Company Name"
            value={settings.sender_name}
            onChange={(e) => setSettings(s => ({ ...s, sender_name: e.target.value }))}
            data-testid="sender-name-input"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-email-settings-btn">
          {saving ? <CircleNotch size={16} className="mr-2 animate-spin" /> : <Envelope size={16} className="mr-2" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        {settings.is_configured && (
          <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-email-btn">
            {testing ? <CircleNotch size={16} className="mr-2 animate-spin" /> : <PaperPlaneTilt size={16} className="mr-2" />}
            {testing ? 'Sending...' : 'Send Test Email'}
          </Button>
        )}
      </div>
    </div>
  );
}
